import { useUpdateAssessmentMutation } from '@/app/redux/endpoints/assessments';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { Assessment, AssessmentStudent } from '@/common/types/assessment';
import { ControllerType } from '@/common/types/student';
import { FeatureSwitch, useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import { ActionLoader } from '@/common/ui/LoadingSpinners';
import PlayButton from '@/common/ui/PlayButton';
import TableInitialsCircle from '@/common/ui/TableInitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import { authenticateStudent } from '@/common/utils/auth';
import { MouseLeftClickIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { TableCell, TableRow } from '@shadcn/components/table';
import { cn } from '@shadcn/lib/utils';
import dayjs from 'dayjs';
import { Keyboard } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AssessmentStudentRowProps {
  studentId: string;
  assessment: Assessment;
  assessmentStudent: AssessmentStudent;
  isLoading: boolean;
  hasResult: boolean;
  onViewResult: () => void;
  inLearnerView?: boolean;
  isShareLink?: boolean;
}
export default function AssessmentStudentRow({
  studentId,
  assessment,
  assessmentStudent,
  hasResult,
  isLoading,
  onViewResult,
  inLearnerView,
  isShareLink,
}: AssessmentStudentRowProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { studentsById } = useStudents();
  const [updateAssessment] = useUpdateAssessmentMutation();

  const { shareLink, student: shareLinkStudent } =
    useAppSelector(selectShareLink);
  const student = shareLinkStudent ?? studentsById.get(studentId);

  const editingDisabled = useMemo(
    () =>
      isShareLink ||
      !isFeatureEnabled('assessments') ||
      !!assessmentStudent.startedAt,
    [assessmentStudent, isShareLink, isFeatureEnabled],
  );

  function handlePhaseChange(phase: number) {
    if (!currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: assessment.id,
      updates: {
        students: {
          ...assessment.students,
          [studentId]: { ...assessmentStudent, phase },
        },
      },
    });
  }

  function handleControlTypeChange(control: ControllerType) {
    if (!currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: assessment.id,
      updates: {
        students: {
          ...assessment.students,
          [studentId]: { ...assessmentStudent, control },
        },
      },
    });
  }

  function handlePlacementChange(placeAtAssessedLevel: boolean) {
    if (!currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: assessment.id,
      updates: {
        students: {
          ...assessment.students,
          [studentId]: { ...assessmentStudent, placeAtAssessedLevel },
        },
      },
    });
  }

  function getStudentName() {
    let studentName = 'Unknown Learner';
    let studentUsername = '';
    if (student) {
      studentName = `${student.studentFirstName} ${student.studentSurname}`;
      studentUsername = student.spellcastUsername;
    }

    return (
      <div className="flex items-center gap-3">
        <TableInitialsCircle userName={`${studentName}`} size={24} />
        <div className="flex flex-col min-w-0 py-1">
          <div className="font-medium text-wrap max-w-[100px]">
            {studentName}
          </div>
          <div className="text-sm text-muted-foreground truncate">
            {studentUsername}
          </div>
        </div>
      </div>
    );
  }

  function getStudentStatus() {
    const { startedAt, completedAt } = assessmentStudent;
    return completedAt ? (
      <Badge className="bg-rag-green">Completed</Badge>
    ) : startedAt ? (
      <Badge className="bg-rag-amber">Started</Badge>
    ) : (
      <Badge variant="secondary">Not Started</Badge>
    );
  }

  async function handleLogin() {
    if (!student) {
      toast.error('Failed to authenticate student', {
        description: 'Student not found',
      });
      return;
    }
    try {
      await authenticateStudent(student.spellcastUsername, student.password);
      navigate('/game');
    } catch (e) {
      console.error(e);
      toast.error('Failed to authenticate student', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function getStudentAction() {
    if (isLoading) {
      return <ActionLoader />;
    }
    const { completedAt } = assessmentStudent;
    if (completedAt && hasResult)
      return (
        <FeatureSwitch
          feature="assessments"
          enabled={
            <Button
              variant="link"
              onClick={(e) => {
                e.stopPropagation();
                onViewResult();
              }}
            >
              View Results
            </Button>
          }
          disabled={
            <Button
              variant="link"
              className="gap-2"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setAttemptedProFeature('Assesssments'));
              }}
            >
              View Results
              <ProIcon />
            </Button>
          }
        />
      );
    return (
      <FeatureSwitch
        feature="game"
        enabled={
          <PlayButton
            onClick={(e) => {
              e.stopPropagation();
              handleLogin();
            }}
          />
        }
        disabled={
          <PlayButton
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setAttemptedProFeature('Spellcaster Game'));
            }}
          >
            Play
            <ProIcon />
          </PlayButton>
        }
      />
    );
  }

  if (!student) return null;
  return (
    <TableRow
      key={student.id}
      className={cn(
        !shareLink && inLearnerView ? 'cursor-pointer' : 'hover:bg-white',
      )}
      onClick={() =>
        !shareLink &&
        inLearnerView &&
        navigate(`/dashboard/assessments/${assessment.id}`)
      }
    >
      <TableCell className="pl-4">
        {inLearnerView
          ? dayjs(assessment.createdAt).format('DD/MM/YY h:mm a')
          : getStudentName()}
      </TableCell>
      <TableCell className="pl-4">{getStudentStatus()}</TableCell>
      <TableCell className="pl-4">
        <Select
          value={assessmentStudent.phase.toString()}
          onValueChange={(value) => handlePhaseChange(Number(value))}
          disabled={editingDisabled}
        >
          <SelectTrigger id="phase" className="min-w-[150px]">
            {assessmentStudent.phase === 0 ? (
              <span className="font-medium">Screener (Phases 1-3)</span>
            ) : (
              <span className="font-medium">
                Phase {assessmentStudent.phase}
              </span>
            )}
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)]">
            <SelectItem value={'0'} className="max-w-md">
              <span className="font-medium">Screener (Phases 1-3)</span>
              <br />
              <span className="text-xs text-muted-foreground">
                Start with a screener to assess all of Phases 1-3 at once.
              </span>
            </SelectItem>
            {scopeAndSequence.phases.map((phase) => (
              <SelectItem key={phase.phase} value={phase.phase.toString()}>
                <span className="font-medium">Phase {phase.phase}</span>
                <br />
                <span className="text-xs text-muted-foreground">
                  {phase.description}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="pl-4">
        <Select
          value={assessmentStudent.control}
          onValueChange={(value) =>
            handleControlTypeChange(value as ControllerType)
          }
          disabled={editingDisabled}
        >
          <SelectTrigger id="control" className="min-w-[150px]">
            <SelectValue placeholder="Select control type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ControllerType.KEYBOARD}>
              <span className="flex items-center gap-2">
                <Keyboard size={16} />
                Typing
              </span>
            </SelectItem>
            <SelectItem value={ControllerType.CLICK}>
              <span className="flex items-center gap-2">
                <MouseLeftClickIcon size={16} />
                Click to Place
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="pl-4">
        <Label
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex items-center gap-2 text-sm w-fit',
            editingDisabled && 'text-muted-foreground',
          )}
        >
          <Checkbox
            checked={assessmentStudent.placeAtAssessedLevel}
            onCheckedChange={(checked) =>
              handlePlacementChange(checked as boolean)
            }
            disabled={editingDisabled}
          />
          <span>Place automatically</span>
        </Label>
      </TableCell>
      <TableCell className="px-4">{getStudentAction()}</TableCell>
    </TableRow>
  );
}
